import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRefreshClientTokenMutation } from '@rsos/reduxDataStore/src/features/videoTo911/videoTo911Api';

const MINUTE = 60 * 1000;
const EXPIRATION_THRESHOLD = 5 * MINUTE;

/**
 * Custom hook that handles refreshing the clientToken when it expires after 1
 * hour and on page load.
 * @param {String} roomId - Room ID
 * @param {String} refreshToken - Refresh token
 */
const useRenewClientToken = ({ roomId, refreshToken }) => {
  const isRenewingRef = useRef(false);
  const setTimeoutRef = useRef(0);
  const [nextTimerTick, setNextTimerTick] = useState(false);

  const clientTokenExpiration = useSelector(
    state => state.videoTo911.rooms?.[roomId],
  )?.client_token_expiry;

  const [triggerRefreshClientToken] = useRefreshClientTokenMutation();

  const renew = useCallback(async () => {
    if (!isRenewingRef.current) {
      try {
        isRenewingRef.current = true;
        await triggerRefreshClientToken({
          roomId,
          refreshToken,
          module: 'telnyx',
        });
      } catch {
        // Suppress the error
      } finally {
        isRenewingRef.current = false;
      }
    }
  }, [triggerRefreshClientToken, roomId, refreshToken]);

  useEffect(() => {
    if (isRenewingRef.current) {
      return;
    }

    (async () => {
      if (roomId && refreshToken) {
        await renew();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextTimerTick, renew, roomId, refreshToken]);

  useEffect(() => {
    // TODO: if response is 401, stop making the requests, otherwise infinite loop in live_video
    if (clientTokenExpiration) {
      const currentTime = new Date().getTime();
      const nextTick =
        clientTokenExpiration - EXPIRATION_THRESHOLD - currentTime;

      setTimeoutRef.current = setTimeout(() => {
        setNextTimerTick(prev => !prev);
      }, nextTick);

      return () => clearTimeout(setTimeoutRef.current);
    }
  }, [clientTokenExpiration]);
};

export default useRenewClientToken;
